// Subir a demanda una foto del lote — ADR-2026-09-08-el-agente-mira-las-fotos-de-a-diez.
//
// Las fotos que el usuario engancha en montón viven como `File` en memoria. Para que el
// modelo las VEA (bloque de imagen del `tool_result`) y para que sirvan de REFERENCIA de
// una generación hace falta lo mismo: que estén en nuestro storage con URL firmada (el
// backend rechaza cualquier referencia que no sea nuestra, `isOurStorageUrl`). Así que las
// dos cosas pasan por acá, y el `storagePath` queda anotado en el registro: mirar una foto
// y después usarla no la sube dos veces.
//
// Se sube sólo lo que alguien pidió de verdad. Un lote de 80 fotos que el agente nunca
// mira no cuesta un solo upload.

import { mapWithConcurrency } from '../../../utils/concurrency';
import { signReferenceImage, uploadReferenceImage } from '../agentAttachments';
import { fotosDelLote, marcarFotoSubida, pathDeFoto } from './lotesDeFotos';

/** Subidas/firmas en vuelo a la vez. Cada una toma una conexión del pool de Storage. */
const CONCURRENCIA = 4;

/**
 * La URL firmada de una foto del lote, subiéndola si es la primera vez. `null` si el lote
 * ya no está en memoria, si el id no existe o si la subida falló — quien llama lo muestra
 * como "no se pudo cargar" en vez de tirar el turno abajo.
 */
export async function urlDeFotoDelLote(
  loteId: string | null | undefined,
  fotoId: string,
  companyId: string | null | undefined,
): Promise<string | null> {
  try {
    const yaSubida = pathDeFoto(loteId, fotoId);
    if (yaSubida) return await signReferenceImage(yaSubida);
    const foto = fotosDelLote(loteId).find((f) => f.id === fotoId);
    if (!foto || !companyId) return null;
    // `uploadReferenceImage` normaliza el formato (HEIC/AVIF → JPEG) antes de subir.
    const { storagePath, previewUrl } = await uploadReferenceImage(foto.archivo, companyId);
    marcarFotoSubida(loteId, fotoId, storagePath);
    return previewUrl || (await signReferenceImage(storagePath));
  } catch {
    return null;
  }
}

/** Varias de una, en orden y con la concurrencia acotada. */
export async function urlsDeFotosDelLote(
  loteId: string | null | undefined,
  fotoIds: string[],
  companyId: string | null | undefined,
): Promise<Array<string | null>> {
  return mapWithConcurrency(fotoIds, CONCURRENCIA, (id) =>
    urlDeFotoDelLote(loteId, id, companyId),
  );
}
